import React from 'react';
import {
  FiCheck, FiChevronDown, FiEye, FiGlobe, FiImage, FiLock, FiMic, FiMusic,
  FiShare2, FiUsers, FiVideo, FiX,
} from 'react-icons/fi';
import { useLanguage } from '../../i18n';
import { BLUE } from './brand';

/**
 * The composer's **“ជម្រើសនៃការបង្ហោះសារ”** panel + the media toolbar.
 *
 *   · media buttons   — Picture · Video · Music · Voice
 *                       (រូបភាព / វីដេអូ / តន្ត្រី / សំឡេង)
 *   · ជម្រើសនៃការបង្ហាញ — public (គ្រប់គ្នាអាចឃើញ) · friends (មិត្តភក្តិ) ·
 *                        only me (ខ្ញុំម្នាក់ប៉ុណ្ណោះ)
 *   · អ្នកដែលអាចឃើញការបង្ហោះនេះ — បិទការទទួលមតិ · បិទការចែករំលែក
 *   · ជម្រើសនៃការចែករំលែក — ចែករំលែកទៅបណ្តាញសង្គមផ្សេងៗទៀត
 *   · publish target  — one of my channels (max 3) or one of my groups
 *
 * Fully controlled: PostComposer owns the state and sends it to the API.
 */
export default function PostOptionsPanel({
  visibility, setVisibility,
  allowComments, setAllowComments,
  allowShare, setAllowShare,
  externalShare, setExternalShare,
  channelId, setChannelId, channels = [],
  groupId, setGroupId, groups = [],
  // the account switcher is on: the article is published as this channel
  lockedChannel = null,
  onPicture, onVideo, onMusic,
  busy = false,
}) {
  const { t, lang } = useLanguage();
  const [open, setOpen] = React.useState(false);

  const VISIBILITY = [
    { key: 'public', icon: <FiGlobe className="w-4 h-4" />, label: t('postPublic'), hint: t('postPublicHint') },
    { key: 'friends', icon: <FiUsers className="w-4 h-4" />, label: t('postFriends'), hint: t('postFriendsHint') },
    { key: 'only_me', icon: <FiLock className="w-4 h-4" />, label: t('postOnlyMe'), hint: t('postOnlyMeHint') },
  ];

  /** One labelled switch (ជម្រើស) — comments / sharing. */
  const Switch = ({ on, onToggle, label, hint, icon }) => (
    <button
      type="button"
      onClick={() => onToggle(!on)}
      className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-left"
    >
      <span className="text-gray-500 shrink-0">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold truncate">{label}</span>
        {hint && <span className="block text-[11px] text-gray-400 truncate">{hint}</span>}
      </span>
      <span className={`w-10 h-5 rounded-full relative transition shrink-0 ${on ? 'bg-[#0B5FDB]' : 'bg-gray-300 dark:bg-gray-600'}`}>
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${on ? 'left-[20px]' : 'left-0.5'}`} />
      </span>
    </button>
  );

  const mediaBtn = 'inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs sm:text-sm font-bold border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800';

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* ── media buttons: Picture · Video · Music ── */}
      <div className="flex items-center gap-2 flex-wrap p-2.5 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700">
        <button type="button" disabled={busy} onClick={onPicture} className={mediaBtn}>
          <FiImage className="w-4 h-4" /> {t('mediaPicture')}
        </button>
        <button type="button" disabled={busy} onClick={onVideo} className={mediaBtn}>
          <FiVideo className="w-4 h-4" /> {t('mediaVideo')}
        </button>
        <button type="button" disabled={busy} onClick={onMusic} className={mediaBtn}>
          <FiMusic className="w-4 h-4" /> {t('mediaMusic')}
        </button>
        <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-gray-400">
          <FiMic className="w-3 h-3" /> {t('mediaVoice')}
        </span>
      </div>

      {/* ── the options: closed by default, one tap opens the panel ── */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-bold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
      >
        <FiChevronDown className={`w-4 h-4 transition ${open ? 'rotate-180' : ''}`} />
        {t('postOptions')}
        <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-semibold text-gray-500">
          {visibility === 'public' && <FiGlobe className="w-3.5 h-3.5" />}
          {visibility === 'friends' && <FiUsers className="w-3.5 h-3.5" />}
          {visibility === 'only_me' && <FiLock className="w-3.5 h-3.5" />}
          <span>
            {visibility === 'public' ? t('badgePublic')
              : visibility === 'friends' ? t('badgeFriends') : t('badgeOnlyMe')}
          </span>
          {!allowComments && <FiX className="w-3 h-3" />}
          {!allowShare && <FiShare2 className="w-3 h-3 opacity-40" />}
        </span>
      </button>

      {open && (
        <div className="p-3 space-y-3.5 border-t border-gray-200 dark:border-gray-700">
          {/* ជម្រើសនៃការបង្ហាញ */}
          <div>
            <p className="text-xs font-extrabold uppercase text-gray-500 mb-1.5">
              {t('visibilityLabel')}
            </p>
            <div className="space-y-1.5">
              {VISIBILITY.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => setVisibility(v.key)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-left ${
                    visibility === v.key
                      ? 'border-transparent text-white'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                  style={visibility === v.key ? { background: BLUE } : undefined}
                >
                  <span className="shrink-0">{v.icon}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-bold truncate">{v.label}</span>
                    <span className={`block text-[11px] truncate ${visibility === v.key ? 'text-white/80' : 'text-gray-400'}`}>
                      {v.hint}
                    </span>
                  </span>
                  {visibility === v.key && <FiCheck className="w-4 h-4 shrink-0" />}
                </button>
              ))}
            </div>
          </div>

          {/* អ្នកដែលអាចឃើញការបង្ហោះនេះ */}
          <div className="space-y-1.5">
            <p className="text-xs font-extrabold uppercase text-gray-500">{t('whoCanSee')}</p>
            <Switch
              on={!allowComments}
              onToggle={(off) => setAllowComments(!off)}
              icon={<FiEye className="w-4 h-4" />}
              label={t('turnOffComments')}
            />
            <Switch
              on={!allowShare}
              onToggle={(off) => setAllowShare(!off)}
              icon={<FiShare2 className="w-4 h-4" />}
              label={t('turnOffShare')}
            />
          </div>

          {/* ជម្រើសនៃការចែករំលែក */}
          <div className="space-y-1.5">
            <p className="text-xs font-extrabold uppercase text-gray-500">{t('shareOptions')}</p>
            <Switch
              on={!!externalShare}
              onToggle={(on) => setExternalShare(on)}
              icon={<FiShare2 className="w-4 h-4" />}
              label={t('shareToSocial')}
              hint={t('shareToSocialHint')}
            />
          </div>

          {/* where it is published: one of my channels (max 3) or one of my groups */}
          <div className="grid sm:grid-cols-2 gap-2">
            <label className="block">
              <span className="block text-[11px] font-bold text-gray-500 mb-1">{t('postToChannel')}</span>
              {lockedChannel ? (
                <span className="block rounded-xl border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-900/20 px-2.5 py-2 text-sm font-bold text-amber-800 dark:text-amber-200 truncate">
                  {t('publishAsChannel')} · {lockedChannel.name}
                </span>
              ) : (
                <select
                  value={channelId || ''}
                  onChange={(e) => setChannelId(e.target.value ? Number(e.target.value) : null)}
                  className="w-full rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 px-2.5 py-2 text-sm"
                >
                  <option value="">{t('channelEmpty')}</option>
                  {channels.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} · @{c.nickname || c.slug}
                    </option>
                  ))}
                </select>
              )}
            </label>
            <label className="block">
              <span className="block text-[11px] font-bold text-gray-500 mb-1">{t('groupsNav')}</span>
              <select
                value={groupId || ''}
                onChange={(e) => setGroupId(e.target.value ? Number(e.target.value) : null)}
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 px-2.5 py-2 text-sm"
              >
                <option value="">{t('groupEmpty')}</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.is_public ? '🌍 ' : '🔒 '}{g.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <p className="text-[11px] text-gray-400">
            {lang === 'kh'
              ? 'អត្ថបទបង្ហោះក្នុងក្រុមនឹងបង្ហាញតែក្នុងក្រុមនោះ។'
              : 'An article published in a group only shows inside that group.'}
          </p>
        </div>
      )}
    </div>
  );
}
